// Ancien abonnement retiré
import { SepaGuard } from '@/components/guards/SepaGuard';
import { useDomainDetail, useDomainFolders } from '@/hooks/useDomains';
import { useDomainsStore } from '@/store/domains.store';
import { Folder } from '@/types/domain.types';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  Check,
  FileText,
  Mail,
  Server,
  Settings,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useArchiveDomain } from '@/hooks/useOrganisations';
import { useCurrentMemberRole } from '@/hooks/useCurrentMemberRole';
import {
  AutomationDrawer,
  BulkAutomationDrawer,
  BulkEditionsDrawer,
  FolderItem,
  FoldersEmptyState,
  FoldersPageSizeSelector,
  FoldersPagination,
  FoldersResponsableSelect,
  FoldersSearchBar,
  FoldersSortDropdown,
  FoldersStatusFilter,
  SenderConfigDisplay,
  SenderConfigModal,
} from '../components';
import { DocumentsView } from '@/features/ged';

export const DomainDetailPage = () => {
  const { t } = useTranslation();
  const { domainUuid } = useParams<{ domainUuid: string }>();
  const {
    clearFolders,
    selectedFolders,
    toggleFolderSelection,
    selectAllFolders,
    clearFolderSelection,
    isSelected,
  } = useDomainsStore();

  // Ancien abonnement retiré

  // Automation drawer state
  const [isAutomationDrawerOpen, setIsAutomationDrawerOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

  // Bulk automation drawer state
  const [isBulkAutomationDrawerOpen, setIsBulkAutomationDrawerOpen] =
    useState(false);

  // Subscription modal state
  // Ancien modal d'abonnement retiré

  // Sender configuration modal state
  const [isSenderConfigModalOpen, setIsSenderConfigModalOpen] = useState(false);

  // Documents modal state
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);

  // Bulk editions drawer state
  const [isBulkEditionsDrawerOpen, setIsBulkEditionsDrawerOpen] = useState(false);
  const [bulkEditionsFolderUuids, setBulkEditionsFolderUuids] = useState<string[]>([]);
  const [bulkEditionsFolderNames, setBulkEditionsFolderNames] = useState<string[]>([]);

  // Archive domain
  const { mutate: archiveDomain, isPending: isArchiving } = useArchiveDomain();
  const { isAdmin } = useCurrentMemberRole();

  // Bulk selection mode
  const [isBulkMode, setIsBulkMode] = useState(false);

  const handleAutomationClick = (folder: Folder) => {
    setSelectedFolder(folder);
    setIsAutomationDrawerOpen(true);
  };

  const handleDataRefresh = () => {
    refetchFolders();
  };

  const handleCloseAutomationDrawer = () => {
    setIsAutomationDrawerOpen(false);
    setSelectedFolder(null);
  };

  const handleBulkAutomationClick = () => {
    setIsBulkAutomationDrawerOpen(true);
  };

  const handleCloseBulkAutomationDrawer = () => {
    setIsBulkAutomationDrawerOpen(false);
  };

  const handleToggleBulkMode = () => {
    setIsBulkMode(!isBulkMode);
    if (isBulkMode) {
      clearFolderSelection();
    }
  };

  const handleSelectAll = () => {
    if (selectedFolders.size === folders.length) {
      clearFolderSelection();
    } else {
      selectAllFolders(folders.map(folder => folder.uuid));
    }
  };

  const {
    domain,
    isLoading: isDomainLoading,
    error: domainError,
  } = useDomainDetail(domainUuid!);
  const {
    folders,
    isLoading: isFoldersLoading,
    error: foldersError,
    refetch: refetchFolders,
  } = useDomainFolders(domainUuid!);

  // Get selected folders objects
  const selectedFoldersObjects = useMemo(() => {
    return folders.filter(folder => selectedFolders.has(folder.uuid));
  }, [folders, selectedFolders]);

  // Clear folders when leaving the page
  useEffect(() => {
    return () => {
      clearFolders();
      clearFolderSelection();
    };
  }, [clearFolders, clearFolderSelection]);

  if (isDomainLoading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="animate-pulse">
          <div className="mb-4 flex items-center gap-4 sm:mb-6">
            <div className="h-6 w-6 rounded-lg bg-neutral-200 sm:h-8 sm:w-8"></div>
            <div className="h-6 w-32 rounded bg-neutral-200 sm:h-8 sm:w-48"></div>
          </div>
          <div className="mb-6 h-24 rounded-2xl bg-neutral-200 sm:mb-8 sm:h-32"></div>
          <div className="space-y-3 sm:space-y-4">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-12 rounded-lg bg-neutral-200 sm:h-16"
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (domainError || !domain) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex items-center gap-4">
          <Link
            to="/domains"
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="hidden sm:inline">
              {t('domains.back.long', 'Back to Domains')}
            </span>
            <span className="sm:hidden">{t('domains.back.short', 'Back')}</span>
          </Link>
        </div>

        <div className="border-red-200 bg-error-50 rounded-2xl border p-8 text-center sm:p-12">
          <div className="bg-error-100 mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full sm:h-16 sm:w-16">
            <Users className="text-error-600 h-6 w-6 sm:h-8 sm:w-8" />
          </div>
          <h3 className="text-error-900 mb-2 text-lg font-semibold">
            {t('domains.detail.error.title', 'Failed to load domain')}
          </h3>
          <p className="text-error-700 mx-auto max-w-md text-sm sm:text-base">
            {typeof domainError === 'string'
              ? domainError
              : t('domains.detail.error.description', 'Please try again later')}
          </p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected':
        return 'bg-success-100 text-success-800 border-success-200';
      case 'disconnected':
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
      case 'error':
        return 'bg-error-100 text-error-800 border-red-200';
      case 'syncing':
        return 'bg-warning-100 text-warning-800 border-warning-200';
      default:
        return 'bg-neutral-100 text-neutral-800 border-neutral-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'connected':
        return t('domains.status.connected', 'Connected');
      case 'disconnected':
        return t('domains.status.disconnected', 'Disconnected');
      case 'error':
        return t('domains.status.error', 'Error');
      case 'syncing':
        return t('domains.status.syncing', 'Syncing');
      default:
        return status;
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/domains"
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900"
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="hidden sm:inline">
            {t('domains.back.long', 'Back to Domains')}
          </span>
          <span className="sm:hidden">{t('domains.back.short', 'Back')}</span>
        </Link>
      </div>

      {/* Domain Info Card */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex min-w-0 flex-1 flex-col gap-4 sm:flex-row sm:items-start">
            <div className="bg-primary-100 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl">
              <Server className="text-primary-600 h-6 w-6" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                <h1 className="truncate text-xl font-bold text-neutral-900 sm:text-2xl">
                  {domain.name}
                </h1>
                <span
                  className={`inline-flex items-center gap-1 self-start rounded-full border px-3 py-1 text-sm font-medium ${getStatusColor(domain.status)} sm:self-auto`}
                >
                  {getStatusText(domain.status)}
                </span>
                {domain.isArchived && (
                  <span className="inline-flex items-center gap-1 self-start rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-sm font-medium text-amber-700 sm:self-auto">
                    <Archive className="h-3.5 w-3.5" />
                    {t('domains.status.archived', 'Archivé')}
                  </span>
                )}
                {isAdmin && (
                  <button
                    onClick={() => archiveDomain({ domainUuid: domain.uuid, archive: !domain.isArchived })}
                    disabled={isArchiving}
                    className={`inline-flex items-center gap-2 self-start rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors sm:self-auto ${
                      domain.isArchived
                        ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                        : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100'
                    } disabled:opacity-50`}
                  >
                    {domain.isArchived ? (
                      <>
                        <ArchiveRestore className="h-4 w-4" />
                        {t('domains.actions.unarchive', 'Désarchiver')}
                      </>
                    ) : (
                      <>
                        <Archive className="h-4 w-4" />
                        {t('domains.actions.archive', 'Archiver')}
                      </>
                    )}
                  </button>
                )}
              </div>
              <p className="mb-4 text-sm text-neutral-600 sm:text-base">
                {domain.description}
              </p>

              <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
                <div>
                  <p className="font-medium text-neutral-500">
                    {t('domains.folders.title', 'Folders')}
                  </p>
                  <p className="mt-1 text-lg font-bold text-neutral-900">
                    {domain.foldersCount}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-neutral-500">
                    {t('domains.lastSync', 'Last Sync')}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-neutral-900">
                    {formatDate(domain.lastSyncAt)}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-neutral-500">
                    {t('domains.syncStatus', 'Status')}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-neutral-900">
                    {domain.lastSyncStatus || '-'}
                  </p>
                </div>
                <div>
                  <p className="font-medium text-neutral-500">
                    {t('domains.autoSync', 'Auto Sync')}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-neutral-900">
                    {domain.autoSync
                      ? t('common.yes', 'Yes')
                      : t('common.no', 'No')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documents Quick Access */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
              <Archive className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t('documents.title', 'Documents')}
              </h3>
              <p className="text-sm text-neutral-600">
                {t('documents.empty.description', 'Gérez les documents de ce domaine')}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsDocumentsModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-primary-700"
          >
            <Archive className="h-4 w-4" />
            {t('documents.title', 'Accéder aux documents')}
          </button>
        </div>
      </div>

      {/* Sender Configuration Section */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-100">
              <Mail className="h-5 w-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t('senderConfig.title', 'Configuration Email')}
              </h3>
              <p className="text-sm text-neutral-600">
                {t(
                  'senderConfig.description',
                  "Configurez l'expéditeur email pour ce domaine"
                )}
              </p>
            </div>
          </div>
          <button
            onClick={() => setIsSenderConfigModalOpen(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <Settings className="h-4 w-4" />
            {t('senderConfig.configure', 'Configurer')}
          </button>
        </div>
        <SenderConfigDisplay
          senderConfigurations={domain.senderConfigurations}
          onConfigure={() => setIsSenderConfigModalOpen(true)}
          showConfigureButton={false}
        />
      </div>
      {/* Folders Section */}
      <div className="space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-lg font-bold text-neutral-900 sm:text-xl">
            {t('domains.folders.title', 'Folders')} ({domain.foldersCount})
          </h2>

          {/* Bulk Actions */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Bulk editions - selected folders */}
            {isBulkMode && selectedFolders.size > 0 && (
              <SepaGuard>
                <button
                  onClick={() => {
                    const selected = folders.filter(f => selectedFolders.has(f.uuid));
                    setBulkEditionsFolderUuids(selected.map(f => f.uuid));
                    setBulkEditionsFolderNames(selected.map(f => f.name));
                    setIsBulkEditionsDrawerOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700"
                >
                  <FileText className="h-4 w-4" />
                  {t('editions.bulk.generateSelected', 'Générer éditions ({{count}})', {
                    count: selectedFolders.size,
                  })}
                </button>
              </SepaGuard>
            )}

            {isBulkMode && selectedFolders.size > 0 && (
              <SepaGuard>
                <button
                  onClick={handleBulkAutomationClick}
                  className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
                >
                  <Settings className="h-4 w-4" />
                  {t('automation.bulk.configure', 'Configure {{count}} folders', {
                    count: selectedFolders.size,
                  })}
                </button>
              </SepaGuard>
            )}

            {/* Bulk editions - all page folders */}
            {!isBulkMode && folders.length > 0 && (
              <SepaGuard>
                <button
                  onClick={() => {
                    setBulkEditionsFolderUuids(folders.map(f => f.uuid));
                    setBulkEditionsFolderNames(folders.map(f => f.name));
                    setIsBulkEditionsDrawerOpen(true);
                  }}
                  className="flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100"
                >
                  <FileText className="h-4 w-4" />
                  {t('editions.bulk.generate', 'Générer éditions')}
                </button>
              </SepaGuard>
            )}

            <button
              onClick={handleToggleBulkMode}
              className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${isBulkMode
                  ? 'bg-neutral-700 text-white hover:bg-neutral-800'
                  : 'border border-neutral-300 text-neutral-700 hover:bg-neutral-50'
                }`}
            >
              {isBulkMode ? (
                <>
                  <X className="h-4 w-4" />
                  {t('common.cancel', 'Cancel')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {t('domains.folders.bulkMode', 'Bulk Mode')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
          <FoldersSearchBar />
          <FoldersStatusFilter />
          <FoldersResponsableSelect options={domain.responsables || []} />
          <FoldersSortDropdown />
          <FoldersPageSizeSelector />
          {isBulkMode && (
            <button
              onClick={handleSelectAll}
              className="flex items-center justify-center gap-2 rounded-lg border border-neutral-300 px-3 py-2.5 text-sm transition-colors hover:bg-neutral-50"
            >
              {selectedFolders.size === folders.length ? (
                <>
                  <X className="h-4 w-4" />
                  {t('common.deselectAll', 'Deselect All')}
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  {t('common.selectAll', 'Select All')}
                </>
              )}
            </button>
          )}
        </div>

        {/* Status indicator */}
        {isBulkMode && selectedFolders.size > 0 && (
          <div className="text-sm text-neutral-600">
            <span className="text-primary-600">
              {selectedFolders.size} {t('common.selected', 'selected')} /{' '}
              {folders.length} {t('domains.folders.title', 'folders')}
            </span>
          </div>
        )}

        {/* Folders List */}
        {isFoldersLoading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="h-14 animate-pulse rounded-lg bg-neutral-200 sm:h-16"
              ></div>
            ))}
          </div>
        ) : foldersError ? (
          <div className="border-red-200 bg-error-50 rounded-xl border p-6 text-center sm:p-8">
            <h3 className="text-error-900 mb-2 font-semibold">
              {t('domains.folders.error.title', 'Failed to load folders')}
            </h3>
            <p className="text-error-700 text-sm sm:text-base">
              {typeof foldersError === 'string'
                ? foldersError
                : 'Failed to load folders'}
            </p>
          </div>
        ) : folders.length === 0 ? (
          <FoldersEmptyState />
        ) : (
          <div className="space-y-2 sm:space-y-3">
            {folders.map(folder => (
              <FolderItem
                key={folder.uuid}
                folder={folder}
                onAutomationClick={handleAutomationClick}
                configurationGuard={SepaGuard}
                showCheckbox={isBulkMode}
                isSelected={isSelected(folder.uuid)}
                onToggleSelection={toggleFolderSelection}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {folders.length > 0 && <FoldersPagination />}
      </div>

      {/* Automation Drawer */}
      <AutomationDrawer
        isOpen={isAutomationDrawerOpen}
        onClose={handleCloseAutomationDrawer}
        folder={selectedFolder}
        onDataRefresh={handleDataRefresh}
      />

      {/* Bulk Automation Drawer */}
      <BulkAutomationDrawer
        isOpen={isBulkAutomationDrawerOpen}
        onClose={handleCloseBulkAutomationDrawer}
        selectedFolders={selectedFoldersObjects}
        onDataRefresh={handleDataRefresh}
      />

      {/* Bulk Editions Drawer */}
      <BulkEditionsDrawer
        isOpen={isBulkEditionsDrawerOpen}
        onClose={() => setIsBulkEditionsDrawerOpen(false)}
        folderUuids={bulkEditionsFolderUuids}
        folderNames={bulkEditionsFolderNames}
      />

      {/* Ancien modal d'abonnement retiré */}

      {/* Sender Configuration Modal */}
      <SenderConfigModal
        isOpen={isSenderConfigModalOpen}
        onClose={() => setIsSenderConfigModalOpen(false)}
        domainUuid={domainUuid}
        currentConfig={domain.senderConfigurations?.[0]}
        onSave={() => {
          // Les données seront automatiquement mises à jour via React Query
          setIsSenderConfigModalOpen(false);
        }}
      />

      {/* Documents Modal */}
      {isDocumentsModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="fixed inset-0 bg-neutral-900/50 backdrop-blur-sm" onClick={() => setIsDocumentsModalOpen(false)} />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative w-full max-w-7xl transform rounded-2xl bg-white p-6 shadow-2xl transition-all">
              <button
                onClick={() => setIsDocumentsModalOpen(false)}
                className="absolute right-4 top-4 rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-neutral-900">
                  {t('documents.title', 'Documents')} - {domain.name}
                </h2>
                <p className="text-sm text-neutral-600">
                  {t('documents.empty.description', 'Gérez les documents de ce domaine')}
                </p>
              </div>
              <DocumentsView
                context="domain"
                contextUuid={domainUuid!}
                contextName={domain.name}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
